/**
 * Persistent red banner shown across every page while an impersonation
 * session is active. Reads `pollpak_impersonator_admin_email` from
 * sessionStorage — set when admin clicks "View as user" on /admin/users.
 *
 * Exit:
 *   1. Pulls the original admin token from sessionStorage.pollpak_admin_token
 *   2. Restores it as the active session
 *   3. Hits /api/admin/impersonate/exit to log the end
 *   4. Reloads to /admin/users so the admin lands back where they started
 */
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function ImpersonationBanner() {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const t = sessionStorage.getItem("pollpak_impersonation_target");
    if (t) {
      try { setTarget(JSON.parse(t)); } catch (_) { setTarget(null); }
    }
    // React to storage changes (e.g. when admin starts impersonation in same tab)
    const onStorage = () => {
      const v = sessionStorage.getItem("pollpak_impersonation_target");
      setTarget(v ? JSON.parse(v) : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const exit = async () => {
    try { await api.post("/admin/impersonate/exit"); } catch (_) {}
    const adminTok = sessionStorage.getItem("pollpak_admin_token");
    const adminUserRaw = sessionStorage.getItem("pollpak_admin_user_cache");
    sessionStorage.removeItem("pollpak_impersonation_target");
    sessionStorage.removeItem("pollpak_admin_token");
    sessionStorage.removeItem("pollpak_admin_user_cache");
    if (adminTok) {
      localStorage.setItem("pollpak_token", adminTok);
    } else {
      localStorage.removeItem("pollpak_token");
    }
    // Restore the admin's user cache so AuthProvider hydrates synchronously
    // back into the admin identity instead of bouncing through `null → /auth/me
    // returns admin` and briefly redirecting to /login.
    if (adminUserRaw) {
      localStorage.setItem("pollpak_user_cache", adminUserRaw);
    } else {
      localStorage.removeItem("pollpak_user_cache");
    }
    // Wipe the patron's API + CMS caches so admin doesn't briefly see patron
    // dashboard residue when /admin/users loads.
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("oodel_api_v1::") || k === "oodel-site-content-v1") localStorage.removeItem(k);
      });
    } catch (_) {}
    window.location.href = "/admin/users";
  };

  // While the banner is mounted, reserve 40px at the top of <body> so that
  // patron / admin sticky nav bars don't get covered by the fixed red bar.
  // Cleared automatically when the banner unmounts (no target) or impersonation
  // ends — single source of truth for the offset.
  useEffect(() => {
    if (!target) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = "40px";
    document.body.classList.add("oodel-impersonating");
    return () => {
      document.body.style.paddingTop = prev;
      document.body.classList.remove("oodel-impersonating");
    };
  }, [target]);

  if (!target) return null;

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm shadow-lg"
    >
      <div className="flex items-center gap-2">
        <span className="font-black tracking-wider text-xs uppercase bg-white text-red-700 px-2 py-0.5">Impersonating</span>
        <span className="font-bold">{target.name || target.email}</span>
        <span className="text-red-100 text-xs">· session ends in 30 min · all actions logged</span>
      </div>
      <button
        onClick={exit}
        data-testid="impersonation-exit"
        className="bg-white text-red-700 hover:bg-red-100 font-bold px-3 py-1 text-xs uppercase tracking-wider"
      >
        Exit impersonation →
      </button>
    </div>
  );
}
